import type { IntlShape } from 'react-intl';
import type { ChatItem, ToolUseItem } from '../../../../contracts/harness.types';
import type { ObjectCreationKind } from '../../../../contracts/projects/object-creation.types';
import { readToolResultJsonCandidates } from '../../../../contracts/toolResult';
import type { Artifact } from '../../artifacts/state';
import { toBlockStatus } from '../genericToolUseStatus';
import { untitledTitle } from './objectToolMapping';
import { directObjectTitle, resultTitleKeysFor, titleFromRecord } from './objectWrites';

const SET_SESSION_TITLE_TOOL = 'set_session_title';
const RENAME_SESSION_DOCUMENT_TOOL = 'rename_session_document';

const DOCUMENT_RENAME_TITLE_KEYS = [
  'title',
  'name',
  'document_title',
  'new_name',
  'new_title',
] as const;

/** Localization, the timeline used for late rename lookups, and the artifact deriver. */
export interface ResolveCreatedTitleDeps {
  intl: IntlShape;
  /**
   * Full timeline — used to resolve a created object's title from a later
   * tool call (`set_session_title` / `rename_session_document`).
   */
  items?: ChatItem[];
  /**
   * Reads the created session or document id off the create call. The app
   * binds it, because the deriver's ports (the slide-builder flag and the
   * session tabs) are app-resolved.
   */
  resolveArtifact: (item: ToolUseItem) => Artifact | undefined;
}

/** Created session id, parsed from the create tool's result. */
const sessionIdOf = (
  item: ToolUseItem,
  resolveArtifact: ResolveCreatedTitleDeps['resolveArtifact']
): string | undefined => {
  const artifact = resolveArtifact(item);
  return artifact?.type === 'session' ? artifact.sessionId : undefined;
};

/**
 * Created document id from the create tool's artifact. Document creates
 * (`create_session_document`, `generate_note_from_template`) open a session
 * pinned to the new doc, so the id lives on `focusDocumentId`.
 */
const documentIdOf = (
  item: ToolUseItem,
  resolveArtifact: ResolveCreatedTitleDeps['resolveArtifact']
): string | undefined => {
  const artifact = resolveArtifact(item);
  if (!artifact) return undefined;
  return artifact.type === 'document'
    ? artifact.documentId
    : artifact.type === 'session'
      ? artifact.focusDocumentId
      : undefined;
};

/** Latest title applied to a session by a completed `set_session_title` call. */
const lateSessionTitle = (
  items: ChatItem[] | undefined,
  sessionId: string
): string | undefined => {
  if (!items) return undefined;
  let title: string | undefined;
  for (const candidate of items) {
    if (candidate.kind !== 'tool_use') continue;
    if (candidate.name !== SET_SESSION_TITLE_TOOL) continue;
    if (toBlockStatus(candidate.status) !== 'success') continue;
    const input = candidate.input ?? {};
    if (input.session_id !== sessionId) continue;
    const next = typeof input.title === 'string' ? input.title.trim() : '';
    if (next) title = next;
  }
  return title;
};

/** First non-empty rename title from a `rename_session_document` input. */
const documentRenameTitle = (input: Record<string, unknown>): string | undefined => {
  for (const key of DOCUMENT_RENAME_TITLE_KEYS) {
    const raw = input[key];
    const next = typeof raw === 'string' ? raw.trim() : '';
    if (next) return next;
  }
  return undefined;
};

/** Latest title applied to a document by a completed `rename_session_document`. */
const lateDocumentTitle = (
  items: ChatItem[] | undefined,
  documentId: string
): string | undefined => {
  if (!items) return undefined;
  let title: string | undefined;
  for (const candidate of items) {
    if (candidate.kind !== 'tool_use') continue;
    if (candidate.name !== RENAME_SESSION_DOCUMENT_TOOL) continue;
    if (toBlockStatus(candidate.status) !== 'success') continue;
    const input = candidate.input ?? {};
    if (input.document_id !== documentId) continue;
    const next = documentRenameTitle(input);
    if (next) title = next;
  }
  return title;
};

const titleFromToolResult = (
  item: ToolUseItem,
  kind: ObjectCreationKind
): string | undefined => {
  for (const candidate of readToolResultJsonCandidates(item)) {
    const title = titleFromRecord(candidate, resultTitleKeysFor(item.name, kind));
    if (title) return title;
  }
  return undefined;
};

/**
 * Display title for a created object. First non-empty wins:
 * 1. Latest successful late rename (`set_session_title` by session_id,
 *    `rename_session_document` by document_id)
 * 2. Tool result payload (`titleFromToolResult`)
 * 3. Create-call input (`directObjectTitle`, including `template_name`)
 * 4. "Untitled …"
 *
 * NOTE: this MAY return PHI (patient name). It is for authenticated in-app
 * display only — never route it into logs, analytics events, or URLs.
 */
export const resolveCreatedTitle = (
  item: ToolUseItem,
  kind: ObjectCreationKind,
  deps: ResolveCreatedTitleDeps
): string => {
  if (kind === 'session') {
    const sessionId = sessionIdOf(item, deps.resolveArtifact);
    if (sessionId) {
      const late = lateSessionTitle(deps.items, sessionId);
      if (late) return late;
    }
  }

  if (kind === 'document') {
    const documentId = documentIdOf(item, deps.resolveArtifact);
    if (documentId) {
      const late = lateDocumentTitle(deps.items, documentId);
      if (late) return late;
    }
  }

  const fromResult = titleFromToolResult(item, kind);
  if (fromResult) return fromResult;

  const direct = directObjectTitle(item, kind);
  if (direct) return direct;

  return untitledTitle(kind, deps.intl);
};
